import { useState, useRef, useEffect } from "react"
import { useStudioActions } from "../../hooks/useStudioActions"
import { 
  FiUser, FiMessageSquare, FiTrendingUp, FiActivity, FiCheckCircle, 
  FiLoader, FiUploadCloud, FiFileText, FiX, FiZap, FiArrowRight, 
  FiShield, FiMic, FiVideo, FiBarChart2, FiAward, FiStar, FiChevronRight, FiSend,
  FiCpu, FiTarget, FiSmile, FiAlertCircle
} from "react-icons/fi"
import { useNotification } from "../../context/NotificationContext"

export default function CareerSync() {
  const [file, setFile] = useState<File | null>(null)
  const [resumeContext, setResumeContext] = useState<string>("")
  const [role, setRole] = useState<string>("")
  const [isInterviewing, setIsInterviewing] = useState(false)
  const [messages, setMessages] = useState<any[]>([])
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [step, setStep] = useState<'setup' | 'interview' | 'feedback'>('setup')
  
  const { analyzeDoc, startInterview, chatWithDoc, evaluateInterview } = useStudioActions()
  const { showNotification } = useNotification()
  const chatEndRef = useRef<HTMLDivElement>(null)

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0]
    if (!selectedFile) return
    setFile(selectedFile)
    setLoading(true)
    
    const formData = new FormData()
    formData.append("file", selectedFile)

    try {
      const data = await analyzeDoc(formData)
      setResumeContext(data.content)
      showNotification("Resume synced successfully!", "success")
    } catch (error: any) {
      showNotification("Failed to sync resume.", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleStartInterview = async () => {
    if (!resumeContext || !role.trim()) {
      showNotification("Please upload resume and specify role.", "warning")
      return
    }
    setLoading(true)
    try {
      const data = await startInterview(resumeContext, role)
      setMessages([{ role: 'assistant', content: data.question }])
      setStep('interview')
      setIsInterviewing(true)
    } catch (error: any) {
      showNotification("Session initiation failed.", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleChat = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!input.trim() || loading) return

    const userMsg = input.trim()
    setInput("")
    setMessages(prev => [...prev, { role: 'user', content: userMsg }])
    setLoading(true)

    try {
      const data = await chatWithDoc(userMsg, resumeContext, messages.slice(-5))
      setMessages(prev => [...prev, { role: 'assistant', content: data.reply }])
    } catch (error: any) {
      showNotification("Error communicating with AI.", "error")
    } finally {
      setLoading(false)
    }
  }

  const handleEndInterview = async () => {
    setLoading(true)
    try {
      const data = await evaluateInterview(messages, resumeContext)
      setFeedback(data.feedback)
      setStep('feedback')
      setIsInterviewing(false)
    } catch (error: any) {
      showNotification("Evaluation failed.", "error")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-vh-100 hero-bg overflow-hidden position-relative pt-5">
      <div className="blob animate-morph bg-primary" style={{ top: '5%', right: '-5%', width: '600px', height: '600px', opacity: 0.08 }}></div>
      <div className="blob animate-morph bg-success" style={{ bottom: '-10%', left: '-5%', width: '500px', height: '500px', opacity: 0.05, animationDelay: '-3s' }}></div>

      <div className="container py-5 mt-5 position-relative z-10">
        {/* Balanced Header */}
        <div className="text-center mb-5 animate-fade-in-up">
           <div className={`badge rounded-pill px-4 py-2 fw-900 mb-3 tracking-wider shadow-sm glass text-success`}>
             {step === 'setup' ? 'CAREER ACCELERATOR' : step === 'interview' ? 'LIVE SESSION' : 'EVALUATION COMPLETE'}
           </div>
           <h2 className="display-4 fw-900 mb-2 tracking-tight text-dark">
             Career-Sync <span className="text-gradient-premium">Interview AI</span>
           </h2>
           {step === 'setup' && <p className="text-muted fw-medium fs-5 max-w-lg mx-auto">Master your interview skills with real-time neural feedback based on your profile.</p>}
        </div>

        <div className="row g-4 justify-content-center">
          <div className="col-xl-11">
            
            {step === 'setup' && (
              <div className="row g-4 animate-scale-in">
                 <div className="col-lg-6">
                    <div className="card-premium glass p-4 p-md-5 h-100 border-0 shadow-2xl">
                       <h4 className="text-dark fw-900 mb-4 d-flex align-items-center gap-3">
                         <FiFileText className="text-success" /> Prepare Profile
                       </h4>
                       
                       <div className="mb-4">
                          <label className="small fw-900 text-muted uppercase tracking-widest mb-2 d-block">Target Role</label>
                          <input 
                            type="text" 
                            className="form-control glass border-0 py-3 px-4 rounded-4 shadow-none fs-5 fw-bold" 
                            placeholder="e.g. Software Engineer"
                            value={role}
                            onChange={(e) => setRole(e.target.value)}
                          />
                       </div>

                       <div className="mb-5">
                          <label className="small fw-900 text-muted uppercase tracking-widest mb-2 d-block">Resume</label>
                          <div 
                            className="upload-area border-2 border-dashed border-success border-opacity-25 rounded-5 p-5 text-center cursor-pointer bg-white bg-opacity-40 hover-scale transition-all"
                            onClick={() => document.getElementById('resume-upload-career')?.click()}
                          >
                             {file ? (
                               <div className="text-success">
                                 <FiCheckCircle size={50} className="mb-3 animate-bounce" />
                                 <h5 className="fw-900 text-truncate px-3">{file.name}</h5>
                                 <span className="small fw-bold opacity-60">Synced & Ready</span>
                               </div>
                             ) : (
                               <div className="text-muted">
                                 <FiUploadCloud size={50} className="mb-3 opacity-30" />
                                 <h5 className="fw-900">Upload Resume</h5>
                                 <p className="small fw-bold opacity-60">Click to browse files</p>
                               </div>
                             )}
                             <input id="resume-upload-career" type="file" className="d-none" onChange={handleResumeUpload} />
                          </div>
                       </div>

                       <button 
                         className="btn-premium w-100 py-4 fs-4 shadow-2xl"
                         disabled={loading || !file || !role.trim()}
                         onClick={handleStartInterview}
                       >
                         {loading ? <FiLoader className="spin me-2" /> : <FiZap className="me-2" />}
                         {loading ? "Starting AI..." : "START INTERVIEW"}
                       </button>
                    </div>
                 </div>

                 <div className="col-lg-6">
                    <div className="card-premium glass p-4 h-100 border-0 shadow-xl overflow-hidden position-relative">
                        <div className="position-absolute top-0 end-0 p-4 opacity-10"><FiCpu size={150} /></div>
                        <h4 className="text-dark fw-900 mb-5 border-bottom border-light pb-3">Session Features</h4>
                        <div className="row g-4">
                           {[
                             { icon: <FiMic />, title: "Adaptive Flow", desc: "Questions adjust based on your previous answers." },
                             { icon: <FiBarChart2 />, title: "Neural Feedback", desc: "Get deep metrics on your technical and soft skills." },
                             { icon: <FiAward />, title: "Role-Specific", desc: "Trained on real interviews for your target position." },
                             { icon: <FiShield />, title: "Privacy First", desc: "Your interview and data are used only for this session." }
                           ].map((feat, i) => (
                             <div key={i} className="col-12">
                                <div className="d-flex gap-4 align-items-center p-3 rounded-5 hover-bg-light transition-all">
                                   <div className="icon-box bg-white shadow-sm flex-shrink-0 text-success">{feat.icon}</div>
                                   <div>
                                      <h6 className="fw-900 text-dark mb-1">{feat.title}</h6>
                                      <p className="small text-muted mb-0 fw-medium">{feat.desc}</p>
                                   </div>
                                </div>
                             </div>
                           ))}
                        </div>
                    </div>
                 </div>
              </div>
            )}

            {step === 'interview' && (
              <div className="row g-4 animate-scale-in">
                 <div className="col-lg-12">
                    <div className="card-premium glass border-0 shadow-2xl overflow-hidden d-flex flex-column" style={{ height: '750px' }}>
                       {/* Header */}
                       <div className="p-4 bg-white border-bottom d-flex justify-content-between align-items-center">
                          <div className="d-flex align-items-center gap-3">
                             <div className="avatar-box bg-primary text-white rounded-circle d-flex align-items-center justify-content-center fw-900 shadow-glow" style={{ width: '45px', height: '45px' }}>AI</div>
                             <div>
                                <h5 className="text-dark fw-900 mb-0">Interviewing for {role}</h5>
                                <span className="badge bg-success bg-opacity-10 text-success x-small fw-bold">SESSION ACTIVE</span>
                             </div>
                          </div>
                          <button className="btn btn-danger rounded-pill px-4 fw-900 shadow-glow border-0" onClick={handleEndInterview}>
                             END & EVALUATE
                          </button>
                       </div>

                       {/* Chat */}
                       <div className="flex-grow-1 overflow-auto p-4 p-md-5 custom-scrollbar d-flex flex-column gap-4" style={{ background: 'rgba(255,255,255,0.4)' }}>
                          {messages.map((msg, i) => (
                            <div key={i} className={`d-flex ${msg.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
                              <div className={`max-w-75 p-4 rounded-5 shadow-sm ${msg.role === 'user' ? 'bg-primary text-white shadow-blue' : 'bg-white text-dark border border-light'}`}>
                                <div className="x-small fw-900 mb-2 uppercase tracking-widest opacity-60">{msg.role === 'user' ? 'You' : 'Interviewer'}</div>
                                <div className="fw-medium fs-5 lh-lg">{msg.content}</div>
                              </div>
                            </div>
                          ))}
                          {loading && (
                            <div className="d-flex justify-content-start">
                              <div className="bg-white p-3 rounded-4 border border-light shadow-sm text-primary fw-900 x-small animate-pulse">
                                AI is analyzing your response...
                              </div>
                            </div>
                          )}
                          <div ref={chatEndRef} />
                       </div>

                       {/* Input */}
                       <div className="p-4 bg-white border-top">
                          <form onSubmit={handleChat} className="position-relative">
                             <input 
                               type="text" 
                               className="form-control bg-light border-0 py-4 px-5 rounded-pill shadow-none fs-5" 
                               placeholder="Speak or type your response..."
                               value={input}
                               onChange={(e) => setInput(e.target.value)}
                               disabled={loading}
                             />
                             <button type="submit" className="btn btn-primary rounded-circle p-3 position-absolute top-50 end-0 translate-middle-y me-2 shadow-glow border-0">
                               <FiSend size={20} />
                             </button>
                          </form>
                       </div>
                    </div>
                 </div>
              </div>
            )}

            {step === 'feedback' && (
               <div className="card-premium glass border-0 shadow-2xl p-4 p-md-5 animate-scale-in printable-document">
                  <div className="d-flex justify-content-between align-items-center mb-5">
                     <div>
                        <h2 className="text-dark fw-900 mb-1">Interview Scorecard</h2>
                        <p className="text-muted fw-bold uppercase tracking-widest mb-0">Analysis by Neural HR Engine</p>
                     </div>
                     <button className="btn btn-primary rounded-pill px-5 py-3 fw-900 shadow-glow border-0 no-print" onClick={() => window.print()}>Save Report</button>
                  </div>

                  <div className="row g-4 mb-5">
                     {[
                       { icon: <FiTrendingUp />, title: "Confidence", score: "82%", color: "primary" },
                       { icon: <FiTarget />, title: "Technical Mastery", score: "75%", color: "success" },
                       { icon: <FiSmile />, title: "Soft Skills", score: "90%", color: "info" },
                       { icon: <FiAlertCircle />, title: "Global Rank", score: "Top 10%", color: "warning" }
                     ].map((stat, i) => (
                       <div key={i} className="col-6 col-md-3">
                          <div className="glass p-4 rounded-5 text-center border-0 shadow-sm bg-white">
                             <div className={`text-${stat.color} mb-2`}>{stat.icon}</div>
                             <h3 className="fw-900 text-dark mb-1">{stat.score}</h3>
                             <span className="small text-muted fw-bold uppercase">{stat.title}</span>
                          </div>
                       </div>
                     ))}
                  </div>

                  <div className="feedback-content custom-scrollbar overflow-auto pe-3 mb-5 bg-white bg-opacity-50 p-4 p-md-5 rounded-5 border" style={{ maxHeight: '500px' }}>
                    {feedback?.split('\n').map((line, i) => {
                       if (line.startsWith('##') || line.startsWith('**')) return <h4 key={i} className="text-dark fw-900 mt-4 mb-3 border-start border-4 border-primary ps-3">{line.replace(/[*#]/g, '').trim()}</h4>
                       if (line.trim() === '') return <br key={i} />;
                       return <p key={i} className="text-muted fw-medium fs-5 lh-lg mb-2">{line.replace(/[*]/g, '')}</p>
                    })}
                  </div>

                  <div className="text-center pt-4 border-top no-print">
                     <button className="btn glass rounded-pill px-5 py-4 fw-900 text-primary border-0 fs-5" onClick={() => setStep('setup')}>
                       <FiZap className="me-2" /> Start New Simulation
                     </button>
                  </div>
               </div>
            )}

          </div>
        </div>
      </div>

      <style>{`
        .upload-area:hover { background: rgba(16, 185, 129, 0.08) !important; border-color: #10b981 !important; }
        .max-w-75 { max-width: 75%; }
        .shadow-blue { box-shadow: 0 10px 30px rgba(79, 70, 229, 0.2); }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .x-small { font-size: 11px; }
      `}</style>
    </div>
  )
}
